import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import CreatorsLanding from "@/pages/CreatorsLanding";
import HealthAudit from "@/pages/HealthAudit";
import Team from "@/pages/Team";
import HowWeVet from "@/pages/HowWeVet";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelect from "@/pages/RoleSelect";
import DemoAccess from "@/pages/DemoAccess";
import CreatorAgreementPage from "@/pages/CreatorAgreementPage";
import GoogleCallback from "@/pages/GoogleCallback";
import BrandJoin from "@/pages/BrandJoin";
import AdminBrandAccess from "@/pages/admin/BrandAccess";
import PaymentReturn from "@/pages/PaymentReturn";
import Placement from "@/pages/Placement";

import BrandOnboarding from "@/pages/brand/Onboarding";
import BrandDashboard from "@/pages/brand/Dashboard";
import BrandMembership from "@/pages/brand/Membership";
import BrandRoster from "@/pages/brand/Roster";
import BrandAccessCode from "@/pages/brand/AccessCode";
import BrandCampaigns from "@/pages/brand/Campaigns";
import BrandNewCampaign from "@/pages/brand/NewCampaign";

import CreatorOnboarding from "@/pages/creator/Onboarding";
import CreatorDashboard from "@/pages/creator/Dashboard";
import CreatorJobs from "@/pages/creator/Jobs";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminVetting from "@/pages/admin/Vetting";
import AdminMatching from "@/pages/admin/Matching";
import AdminAuditLeads from "@/pages/admin/AuditLeads";
import AdminDemo from "@/pages/admin/Demo";
import AdminCreators from "@/pages/admin/Creators";
import AdminUsers from "@/pages/admin/Users";
import AdminJobs from "@/pages/admin/Jobs";
import AdminCampaigns from "@/pages/admin/Campaigns";
import AdminBrands from "@/pages/admin/Brands";
import Pricing from "@/pages/Pricing";

import { ProtectedRoute } from "@/components/yc/ProtectedRoute";

function AppRouter() {
    const location = useLocation();
    // CRITICAL: handle session_id in URL fragment synchronously to prevent race conditions
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/creators" element={<CreatorsLanding />} />
            <Route path="/team" element={<Team />} />
            <Route path="/how-we-vet" element={<HowWeVet />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/health-audit" element={<HealthAudit />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/google" element={<GoogleCallback />} />
            <Route path="/role" element={<RoleSelect />} />
            <Route path="/demo" element={<DemoAccess />} />
            <Route path="/creator-agreement" element={<CreatorAgreementPage />} />
            <Route path="/brands/join" element={<BrandJoin />} />
            <Route path="/payment/return" element={<PaymentReturn />} />

            <Route path="/brand/onboarding" element={<ProtectedRoute role="brand"><BrandOnboarding /></ProtectedRoute>} />
            <Route path="/brand/dashboard" element={<ProtectedRoute role="brand"><BrandDashboard /></ProtectedRoute>} />
            <Route path="/brand/membership" element={<ProtectedRoute role="brand"><BrandMembership /></ProtectedRoute>} />
            <Route path="/brand/roster" element={<ProtectedRoute role="brand"><BrandRoster /></ProtectedRoute>} />
            <Route path="/brand/campaigns" element={<ProtectedRoute role="brand"><BrandCampaigns /></ProtectedRoute>} />
            <Route path="/brand/campaigns/new" element={<ProtectedRoute role="brand"><BrandNewCampaign /></ProtectedRoute>} />
            <Route path="/brand/code" element={<ProtectedRoute role="brand"><BrandAccessCode /></ProtectedRoute>} />

            <Route path="/creator/onboarding" element={<ProtectedRoute role="creator"><CreatorOnboarding /></ProtectedRoute>} />
            <Route path="/creator/dashboard" element={<ProtectedRoute role="creator"><CreatorDashboard /></ProtectedRoute>} />
            <Route path="/creator/jobs" element={<ProtectedRoute role="creator"><CreatorJobs /></ProtectedRoute>} />

            <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/vetting" element={<ProtectedRoute role="admin"><AdminVetting /></ProtectedRoute>} />
            <Route path="/admin/jobs" element={<ProtectedRoute role="admin"><AdminJobs /></ProtectedRoute>} />
            <Route path="/admin/matching" element={<ProtectedRoute role="admin"><AdminMatching /></ProtectedRoute>} />
            <Route path="/admin/campaigns" element={<ProtectedRoute role="admin"><AdminCampaigns /></ProtectedRoute>} />
            <Route path="/admin/brands" element={<ProtectedRoute role="admin"><AdminBrands /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute role="admin"><AdminAuditLeads /></ProtectedRoute>} />
            <Route path="/admin/demo" element={<ProtectedRoute role="admin"><AdminDemo /></ProtectedRoute>} />
            <Route path="/admin/creators" element={<ProtectedRoute role="admin"><AdminCreators /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/brand-access" element={<ProtectedRoute role="admin"><AdminBrandAccess /></ProtectedRoute>} />

            <Route path="/placement/:placementId" element={<ProtectedRoute><Placement /></ProtectedRoute>} />

            <Route path="*" element={<Landing />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Toaster position="top-right" richColors />
                <AppRouter />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
